import { initializeApp, getApps } from 'firebase/app';
import { getAnalytics, isSupported, setUserId } from 'firebase/analytics';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken as getAppCheckSdkToken,
} from 'firebase/app-check';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '',
};

const hasConfig =
  !!firebaseConfig.apiKey &&
  !!firebaseConfig.authDomain &&
  !!firebaseConfig.projectId &&
  !!firebaseConfig.appId &&
  !!firebaseConfig.measurementId;

// Onda 1A hardening 2026-06: App Check com reCAPTCHA Enterprise.
// Sem key configurada, o init eh um no-op (modo backend 'off' / 'observe' nao quebra).
const APPCHECK_KEY = process.env.REACT_APP_APPCHECK_RECAPTCHA_KEY || '';
const APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || '';
let appCheckInstance = null;

let analyticsInstance = null;
let analyticsPromise = null;

const getFirebaseApp = () => {
  if (!hasConfig || typeof window === 'undefined') return null;
  return getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
};

// Onda 1B hardening 2026-06: exposto para o modulo de Auth do admin.
// (firebase/auth importado preguicosamente para nao puxar peso na home.)
export const getFirebaseAppInstance = () => getFirebaseApp();

const resolveAnalytics = async () => {
  if (analyticsPromise) return analyticsPromise;
  analyticsPromise = (async () => {
    if (!hasConfig || typeof window === 'undefined') return null;
    const supported = await isSupported().catch(() => false);
    if (!supported) return null;
    const app = getFirebaseApp();
    if (!app) return null;
    analyticsInstance = getAnalytics(app);
    return analyticsInstance;
  })().catch(() => null);
  return analyticsPromise;
};

/**
 * Inicializa Firebase App Check (reCAPTCHA Enterprise).
 * Idempotente. No-op se REACT_APP_APPCHECK_RECAPTCHA_KEY estiver vazia.
 * Em dev, pode usar REACT_APP_APPCHECK_DEBUG_TOKEN para token de debug
 * registrado no console Firebase.
 */
export const initAppCheck = () => {
  if (typeof window === 'undefined') return null;
  if (appCheckInstance) return appCheckInstance;
  if (!hasConfig) return null;
  if (!APPCHECK_KEY) return null;
  try {
    if (APPCHECK_DEBUG_TOKEN) {
      // eslint-disable-next-line no-restricted-globals
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = APPCHECK_DEBUG_TOKEN;
    }
    const app = getFirebaseApp();
    if (!app) return null;
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APPCHECK_KEY),
      isTokenAutoRefreshEnabled: true,
    });
    return appCheckInstance;
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[appCheck] init falhou:', e?.message || e);
    }
    return null;
  }
};

/**
 * Retorna o token App Check atual (string) ou null. Nunca lanca — falha
 * silenciosamente para nao quebrar UX. Cacheado pelo SDK.
 */
export const fetchAppCheckToken = async () => {
  try {
    const inst = appCheckInstance || initAppCheck();
    if (!inst) return null;
    const t = await getAppCheckSdkToken(inst, /* forceRefresh */ false);
    return t?.token || null;
  } catch {
    return null;
  }
};

export const getAppCheckStatus = () => ({
  configured: !!APPCHECK_KEY,
  initialized: !!appCheckInstance,
});

export const initFirebaseAnalytics = () => resolveAnalytics();

export const getAnalyticsSafe = async () => resolveAnalytics();

export const setAnalyticsUserId = async (userId) => {
  if (!userId) return;
  const analytics = await resolveAnalytics();
  if (analytics) {
    setUserId(analytics, String(userId));
  }
};

export const getFirebaseAnalyticsState = () => ({
  hasConfig,
  hasAnalytics: !!analyticsInstance,
});
